import _ from 'lodash';
import { useState } from 'react';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { CancelSaveAndContinue } from '@/core/CancelSaveAndContinue.molecule';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { Schedule } from '@/schedule/Schedule';
import { ScheduleTypeName } from '@/schedule/schedule.types';
import { getNewCronSchedule, getQuartzCronScheduleDescription } from '@/schedule/schedule.utilities';
import { sqTimezones } from '@/utilities/datetime.constants';
import { faPencil, faTrash } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { InputGroup } from '@seeqdev/qomponents';
import type { Timezone } from '@/datetime/timezone.service';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { UpdatedSchedule } from '@/schedule/schedule.types';
import type { IconType } from '@seeqdev/qomponents';

type ScheduleCronSchedule = string[] | undefined;

export interface ScheduleFormComponentProps extends ValidatingFormComponent<ScheduleCronSchedule> {
  component: 'ScheduleFormComponent';
  customErrorText?: string;
  onDelete: () => void;
  schedule: string;
  customScheduleLabel: string;
  noScheduleMessage: string;
  scheduleMessage: string;
  setIsScheduleUnsaved: (isUnsaved: boolean) => void;
  isScheduleUnsaved: boolean;
  onSave?: (cronSchedule: string[] | undefined, scheduleData: UpdatedSchedule | undefined) => void;
  timezoneDefault?: Timezone;
  deleteIconStyle?: IconType;
}

export const ScheduleFormComponent = (props: ScheduleFormComponentProps) => {
  const {
    name,
    value,
    testId = 'schedule',
    validation,
    extendValidation,
    customErrorText,
    required = false,
    schedule,
    customScheduleLabel,
    noScheduleMessage,
    scheduleMessage,
    onDelete,
    setIsScheduleUnsaved,
    isScheduleUnsaved,
    onSave,
    timezoneDefault = sqTimezones.defaultTimezone,
    deleteIconStyle = 'theme',
  } = props;

  const { t } = useTranslation();

  const [updatedSchedule, setUpdatedSchedule] = useState<UpdatedSchedule>();
  const [newCronSchedule, setNewCronSchedule] = useState<string[]>();

  const formState = useForm().getState();
  const showError = _.has(formState.errors, name) && formState.submitFailed && formState.hasValidationErrors;

  const defaultValidation = (fieldValue: ScheduleCronSchedule): boolean => required && !fieldValue;
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  const newScheduleDescription = getQuartzCronScheduleDescription(newCronSchedule);
  const newScheduleDisplay = newScheduleDescription
    ? `${newScheduleDescription}${timezoneDefault?.displayName ? ` (${timezoneDefault.displayName})` : ''}`
    : undefined;
  const newScheduleLabel = newScheduleDisplay
    ? t(scheduleMessage, { schedule: newScheduleDisplay })
    : t(customScheduleLabel);
  const scheduleLabel = !_.isEmpty(value) ? t(scheduleMessage, { schedule }) : t(noScheduleMessage, { schedule });

  return (
    <>
      <FormFieldWrapper
        wrapperClassNames="flexFill mb20"
        testId={testId}
        showError={showError}
        errorClassNames="ml20"
        customErrorText={customErrorText}>
        <Field name={name} validate={appliedValidation}>
          {() => {
            return (
              <div>
                {!isScheduleUnsaved ? (
                  <InputGroup
                    field={
                      <div
                        data-testid={`${testId}-value`}
                        onClick={() => setIsScheduleUnsaved(true)}
                        className="width-maximum tw:pr-1 tw:pl-1">
                        {newCronSchedule ? newScheduleLabel : `${scheduleLabel}`}
                      </div>
                    }
                    append={[
                      newCronSchedule || value
                        ? {
                            variant: 'button',
                            buttonProps: {
                              icon: faTrash,
                              iconStyle: deleteIconStyle,
                              testId: `${testId}-delete`,
                              onClick: () => {
                                onDelete();
                                setNewCronSchedule(undefined);
                                setUpdatedSchedule(undefined);
                              },
                            },
                          }
                        : undefined,
                      {
                        variant: 'button',
                        buttonProps: {
                          icon: faPencil,
                          iconStyle: 'theme',
                          testId: `${testId}-edit`,
                          onClick: () => setIsScheduleUnsaved(true),
                        },
                      },
                    ]}
                  />
                ) : (
                  <div className="tw:mt-sq-10 tw:mb-5 tw:ml-5">
                    <Schedule
                      cronSchedule={value}
                      initialScheduleType={ScheduleTypeName.LIVE}
                      updatedSchedule={updatedSchedule}
                      onScheduleChange={setUpdatedSchedule}
                      scheduleTypeTranslations={{ [ScheduleTypeName.LIVE]: 'SCHEDULE_TYPE.LIVE.NAME_ALTERNATE' }}
                      timezoneDefault={timezoneDefault}
                    />
                    <CancelSaveAndContinue
                      submitFn={() => {
                        const scheduleToSave = updatedSchedule ? getNewCronSchedule(updatedSchedule) : undefined;
                        if (scheduleToSave) {
                          setNewCronSchedule(scheduleToSave);
                        }
                        setIsScheduleUnsaved(false);
                        onSave?.(scheduleToSave, updatedSchedule);
                      }}
                      cancelFn={() => {
                        setUpdatedSchedule(undefined);
                        setIsScheduleUnsaved(false);
                      }}
                      values={[]}
                      submitClassNames="width-80"
                      cancelClassNames="mr10 width-80"
                    />
                  </div>
                )}
              </div>
            );
          }}
        </Field>
      </FormFieldWrapper>
    </>
  );
};
